import { useCallback, useRef, useState } from 'react';

import { planDisclosureInit } from '../filterPanelPlan';

/**
 * Open/closed state for a filter panel's disclosure sections, held as one record keyed by
 * section id rather than one `useState` per section.
 */
export function useDisclosureOpenState<K extends string>(defaults: Record<K, boolean>) {
  const [openState, setOpenState] = useState<Record<K, boolean>>(defaults);
  const initializedRef = useRef(false);

  const isOpen = useCallback((key: K) => openState[key] ?? false, [openState]);

  const setOpen = useCallback(
    (key: K) => (open: boolean) => {
      setOpenState((prev) => (prev[key] === open ? prev : { ...prev, [key]: open }));
    },
    []
  );

  const openSection = useCallback((key: K) => {
    setOpenState((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);

  /**
   * Once per panel-open, force open every section that already has a selection. Resets when
   * the panel closes so the next open re-runs it.
   */
  const initializeOnce = useCallback(
    (panelOpen: boolean, shouldOpen: Partial<Record<K, boolean>>) => {
      if (!panelOpen) {
        initializedRef.current = false;
        return;
      }
      if (initializedRef.current) return;
      initializedRef.current = true;
      setOpenState((prev) => planDisclosureInit(prev, shouldOpen));
    },
    []
  );

  return { isOpen, setOpen, openSection, initializeOnce };
}
